/// <amd-module name="Wiki/LinkWitPanel/LinkWitPanel" />
import * as React from "react";

import { ITfsPageService } from "Tfs/Platform/Page";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { toLookup } from "VSS/Platform/Util/Array";
import { encodeUri, IArtifactData } from "VSS/Platform/Util/Linking";
import { deepMerge } from "VSS/Platform/Util/Object";
import { IButtonProps } from "VSSUI/Button";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";

import { WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import * as WorkItemTracking from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wiki/Clients/Wit/RestClient/WorkItemTracking";
import { IWorkItemTrackingHttpBatchClient, JsonHttpResponse, JsonPatchDocument } from "Wiki/Clients/WitBatch/Contracts/WitBatchRestClient";
import { getWitBatchClient, getWorkItemPatchAddOperation, getWorkItemPatchRemoveDocument } from "Wiki/Clients/WitBatch/RestClient/WitBatchRestClient";
import * as Resources from "Wiki/LinkWitPanel/Resources";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface ILinkWitPanelProps {
    onDismiss: () => void;
    title: string;
    wikiId: string;
    wikiPagePath: string;
    wikiType: WikiType;
    onUpdateButtonClick: (count: number) => void;
    hasWikiContributePermission?: boolean;
}

export interface ILinkWitPanelState {
    workItemsInDraft: number[];
    savedWorkItems: number[];
    isDirty: boolean;
}

const Tool = "Wiki";
const Type = "WikiPage";

export class LinkWitPanel extends VssComponent<ILinkWitPanelProps, ILinkWitPanelState> {
    private _wikiPageArtifactData: IArtifactData;
    private _projectId: string;

    constructor(props: ILinkWitPanelProps, context: IVssComponentContext) {
        super(props, context);
        const tfsPageData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        this._projectId = tfsPageData && tfsPageData.project ? tfsPageData.project.id : "";
        const wikiPagePath = this.props.wikiPagePath.charAt(0) === "/" ? this.props.wikiPagePath.substring(1) : this.props.wikiPagePath;
        const wikiPageArtifactId = format("{0}/{1}/{2}", this._projectId, this.props.wikiId, wikiPagePath);
        this._wikiPageArtifactData = {
            id: wikiPageArtifactId,
            tool: Tool,
            type: Type,
            uri: encodeUri({ tool: Tool, type: Type, id: wikiPageArtifactId })
        };

        this.state = {
            workItemsInDraft: [],
            savedWorkItems: [],
            isDirty: false
        };
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [{ text: Resources.Cancel, onClick: this.props.onDismiss }];
        if (this.props.hasWikiContributePermission) {
            footerButtons.push({ text: Resources.Update, primary: true, onClick: this._onSave, disabled: !this.state.isDirty });
        }

        return (
            <Panel
                onDismiss={this.props.onDismiss}
                titleProps={{ text: format(Resources.LinkWitPanelHeading, this.props.title) }}
                description={Resources.LinkWitPanelDescription}
                footerButtonProps={footerButtons}
            >
                <div className={"panel-content flex-column"}>
                    <div className={"panel-content-heading"}>{Resources.PanelContentHeading}</div>
                    <LegacyComponent
                        modules={["Wiki/Scenarios/Integration/LinkWorkItems/LinkWorkItemsDialogContent"]}
                        wrappedType="Wiki.LinkWIT.DialogContent"
                        projectId={this._projectId}
                        hostArtifact={this._wikiPageArtifactData}
                        onAddWorkItem={this._addWorkItemToDraft}
                        onRemoveWorkItem={this._onRemoveWorkItem}
                        workItemsInDraft={this.state.workItemsInDraft}
                        readOnly={!this.props.hasWikiContributePermission}
                        loadingComponent={() => {
                            return <Spinner />;
                        }}
                    />
                </div>
            </Panel>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.trackPromise(_getWorkItemIdsForArtifactUri(this._wikiPageArtifactData.uri!, this._projectId, this.context.pageContext)).promise.then(
            (workItemIds: number[]) => {
                if (workItemIds) {
                    this.setState({
                        workItemsInDraft: deepMerge([], workItemIds),
                        savedWorkItems: deepMerge([], workItemIds)
                    });
                }
            }
        );
    }

    private _addWorkItemToDraft = (workItemId: number): void => {
        const workItemsInDraft = this.state.workItemsInDraft;

        if (workItemsInDraft.indexOf(workItemId) === -1) {
            this.setState(() => {
                let workItemsInDraftArray: number[];
                if (workItemsInDraft.indexOf(workItemId) === -1) {
                    workItemsInDraftArray = deepMerge([], workItemsInDraft);
                    workItemsInDraftArray.push(workItemId);
                } else {
                    workItemsInDraftArray = workItemsInDraft;
                }

                return {
                    workItemsInDraft: workItemsInDraftArray,
                    isDirty: this._isDirty(workItemsInDraftArray)
                };
            });
        }
    };

    private _onRemoveWorkItem = (workItem: IArtifactData): void => {
        const workItemId = parseInt(workItem.id);
        const index = this.state.workItemsInDraft.indexOf(workItemId);

        if (index !== -1) {
            const workItemsInDraft = this.state.workItemsInDraft;
            workItemsInDraft.splice(index, 1);
            this.setState(() => {
                let workItemsInDraftArray: number[];
                if (workItemsInDraft.indexOf(workItemId) === -1) {
                    workItemsInDraftArray = deepMerge([], workItemsInDraft);
                } else {
                    workItemsInDraftArray = workItemsInDraft;
                }

                return {
                    workItemsInDraft: workItemsInDraftArray,
                    isDirty: this._isDirty(workItemsInDraftArray)
                };
            });
        }
    };

    private _onSave = (): void => {
        const witBatchClient = getWitBatchClient(this.context.pageContext);
        const workItemsToAdd = this.state.workItemsInDraft.filter((workItem: number) => {
            return this.state.savedWorkItems.indexOf(workItem) < 0;
        });

        const workItemsToRemove = this.state.savedWorkItems.filter((workItem: number) => {
            return this.state.workItemsInDraft.indexOf(workItem) < 0;
        });

        if (workItemsToAdd.length > 0) {
            const operations = workItemsToAdd.map(
                (workItemId: number): [number, JsonPatchDocument] => {
                    return [workItemId, [getWorkItemPatchAddOperation(this._wikiPageArtifactData.uri, "Wiki Page")]];
                }
            );
            witBatchClient.updateWorkItemsBatch(operations).then(() => {
                _removeWorkItemsAsync(this._wikiPageArtifactData.uri, workItemsToRemove, witBatchClient, this.context.pageContext);
            });
        } else {
            _removeWorkItemsAsync(this._wikiPageArtifactData.uri, workItemsToRemove, witBatchClient, this.context.pageContext);
        }
        publish(this.context.pageContext, TelemetryConstants.LinkWorkItemsToWikiPage, this.props.wikiType, {
            AddCount: workItemsToAdd.length,
            RemoveCount: workItemsToRemove.length
        });
        this.props.onUpdateButtonClick(this.state.savedWorkItems.length + workItemsToAdd.length - workItemsToRemove.length);
        this.props.onDismiss();
    };

    private _isDirty(workItemsInDraftArray: number[]): boolean {
        const { savedWorkItems } = this.state;
        const workItemsIntersectionList = _intersect(savedWorkItems, workItemsInDraftArray);

        if (workItemsIntersectionList.length === savedWorkItems.length && workItemsIntersectionList.length === workItemsInDraftArray.length) {
            return false;
        }

        return true;
    }
}

/**
 * Helper method used to intersect arrays of numbers
 * @param arrayA
 * @param arrayB
 * @return
 */
function _intersect(arrayA: number[], arrayB: number[]): number[] {
    if (!arrayB) {
        return arrayA;
    }

    if (!arrayA) {
        return arrayB;
    }

    if (arrayB.length === 0 || arrayA.length === 0) {
        return [];
    }

    let hashtable: { [key: string]: number };
    let arrayC: number[];

    if (arrayA.length < arrayB.length) {
        hashtable = toLookup(arrayA, (n: number) => {
            return n.toString();
        });
        arrayC = arrayB;
    } else {
        hashtable = toLookup(arrayB, (n: number) => {
            return n.toString();
        });
        arrayC = arrayA;
    }

    const result = [];

    for (let i = 0; i < arrayC.length; i++) {
        const value = arrayC[i];
        if (hashtable.hasOwnProperty(value)) {
            result.push(value);
        }
    }

    return result;
}

function _getWorkItemIdsForArtifactUri(artifactUri: string, projectId: string, pageContext: IVssPageContext): Promise<number[]> {
    const artifactUrisQuery = {
        artifactUris: [artifactUri]
    };

    return getWorkItemTrackingClient(pageContext)
        .queryWorkItemsForArtifactUris(artifactUrisQuery, projectId)
        .then((result: WorkItemTracking.ArtifactUriQueryResult) => {
            const workItems = result.artifactUrisQueryResult[artifactUri] || [];
            return workItems.map(workItem => workItem.id);
        });
}

function _removeWorkItemsAsync(
    artifactUri: string | undefined,
    workItemIds: number[],
    witBatchClient: IWorkItemTrackingHttpBatchClient,
    pageContext: IVssPageContext
): Promise<void> | void {
    if (workItemIds.length > 0) {
        // uri will be defined in our case. Check is needed because of type definition
        if (!artifactUri) {
            return Promise.reject();
        }

        if (workItemIds.length === 1) {
            return _removeWorkItemAsync(artifactUri, workItemIds[0], pageContext);
        }

        return getWorkItemTrackingClient(pageContext)
            .getWorkItems(workItemIds, undefined, undefined, undefined, WorkItemTracking.WorkItemExpand.Relations)
            .then((workItems: WorkItemTracking.WorkItem[]) => {
                return workItems.map(workItem => _mapWorkItemToUpdateDocument(workItem, artifactUri)).filter(op => Boolean(op));
            })
            .then((operations: [number, JsonPatchDocument][]) => {
                return witBatchClient.updateWorkItemsBatch(operations);
            })
            .then((responses: JsonHttpResponse[]) => {
                const failedResponses = responses && responses.filter((response: JsonHttpResponse) => response.code >= 400);
                if (failedResponses.length) {
                    const firstBody = JSON.parse(failedResponses[0].body);
                    throw new Error(firstBody && firstBody.value && firstBody.value.Message ? firstBody.value.Message : firstBody);
                }
            });
    }
}

function _removeWorkItemAsync(artifactUri: string, workItemId: number, pageContext: IVssPageContext): Promise<void> {
    const witRestClient = getWorkItemTrackingClient(pageContext);
    return witRestClient
        .getWorkItem(workItemId, undefined, undefined, undefined, WorkItemTracking.WorkItemExpand.Relations)
        .then((workItem: WorkItemTracking.WorkItem) => {
            let serverUri: string | undefined;
            let deleteIndex = -1;

            if (workItem !== null && workItem.relations !== null) {
                // Find the index of the relation to delete
                for (let i = 0; i < workItem.relations.length; ++i) {
                    if (localeIgnoreCaseComparer(workItem.relations[i].url, artifactUri) === 0) {
                        deleteIndex = i;
                        serverUri = workItem.relations[i].url;
                        break;
                    }
                }
            }

            if (deleteIndex >= 0 && serverUri) {
                witRestClient.updateWorkItem(getWorkItemPatchRemoveDocument(deleteIndex, serverUri), workItemId);
            }
        });
}

function _mapWorkItemToUpdateDocument(workItem: WorkItemTracking.WorkItem, artifactUri: string | undefined): [number, JsonPatchDocument] | null {
    if (workItem && workItem.relations && artifactUri) {
        for (let i = 0; i < workItem.relations.length; ++i) {
            if (localeIgnoreCaseComparer(workItem.relations[i].url, artifactUri) === 0) {
                return [workItem.id, getWorkItemPatchRemoveDocument(i, workItem.relations[i].url)];
            }
        }
    }
    return null;
}

VssComponent.register("Wiki.LinkWitPanel", LinkWitPanel);
